import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Accordion, ActionIcon, Anchor, Button, CloseButton, Flex, Group, Loader, Text, Title } from '@mantine/core';
import { IconPrinter, IconRepeat, IconTrash } from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import type { FormErrors } from '@mantine/form';
import { useAtom } from 'jotai';
import { useBlocker } from '@tanstack/react-router';
import Fetch from '@/utils/Fetch';
import Time from '@/utils/time';
import Helper from '@/utils/helper';
import * as Enums from '@/utils/enums';
import Constants from '@/utils/constants';
import ToastContext from '@/utils/toast-context';
import ConfirmAction from '@/components/modals/confirm-action';
import DownloadService from '@/utils/download-service';
import OptionService from '@/services/option/option-service';
import signatureService from '@/services/signature/signature-service';
import ScDrawer from '@/PageComponents/Drawer/ScDrawer';
import { openModalsAtom } from '@/utils/atoms';
import { FormDefinitionField, FormHeader, FormItem } from '@/interfaces/api/models';
import { useFormDefinitionData } from './useFormDefinitionData';
import { useFormAutosave } from './useFormAutosave';
import {
    addSectionGroupInstance,
    buildAutosaveComposite,
    buildSectionTree,
    decodeFormItemValue,
    encodeFormItemValue,
    findSectionKeyForError,
    FormFieldValue,
    getSectionNavKey,
    getSectionNavLabel,
    isSectionGroupAnswered,
    removeSectionGroupInstance,
    sameValues,
    scrollToFirstError,
    sectionStats,
    sectionStatus,
    SectionGroupNode,
    SignaturePreview,
} from './form-helpers';
import { buildValidator } from './form-validate';
import FormSection from './FormSection';
import FormTableOfContents from './FormTableOfContents';
import { SignatureFieldContext } from './FormFieldBinding';
import DraftRecoveryPrompt from './DraftRecoveryPrompt';
import styles from './ManageFormModal.module.css';

interface ManageFormModalProps {
    onSave: (formHeader: FormHeader | null) => void;
    formHeaderToManage: FormHeader;
    isNew: boolean;
    onRenew: (formHeader: FormHeader) => void;
}

/**
 * Older forms can come back from the server with `FormItem`s that predate the denormalised
 * section columns. The legacy editor back-filled them from the form definition inside
 * `getSectionsToRender`; the section tree builder here needs the same guarantee, so it is done
 * once at load instead of on every keystroke.
 */
function hydrateFormItems(items: FormItem[], fields: FormDefinitionField[]): FormItem[] {
    return items.map((item) => {
        if (item.DataType) return item;
        const field = fields.find((x) => x.ID === item.FormDefinitionFieldID);
        if (!field) return item;
        const section = field.Section;
        return {
            ...item,
            DataType: field.DataType,
            Required: field.Required,
            SectionID: field.SectionID,
            SectionHeading: section ? section.Heading : null,
            SectionDescription: section ? section.Description : null,
            SectionRepeatable: section ? section.Repeatable : null,
            SectionDisplayOrder: section ? section.DisplayOrder : null,
            ParentSectionID: section ? section.ParentSectionID : null,
            ParentSectionGroup: item.ParentSectionGroup ?? 0,
        };
    });
}

function valuesFromItems(items: FormItem[]): Record<string, FormFieldValue> {
    const values: Record<string, FormFieldValue> = {};
    items.forEach((item) => {
        values[item.ID] = decodeFormItemValue(item.DataResult, item.DataType ?? '');
    });
    return values;
}

/**
 * The `existing:` form of `initSignature` — the identity of *which* form the store currently holds.
 *
 * It keys on the `FormDefinitionID` **and** the `FormItem`-ID list, and both halves are
 * load-bearing:
 *
 * - the item-ID list catches a genuine change of item set (a structural upgrade, a reload that
 *   regenerates blank item GUIDs);
 * - the `FormDefinitionID` catches an Upgrade where the server hands back the *same* `FormItem`
 *   IDs remapped onto the new definition's fields. That really happens. Without this half the
 *   signature was unchanged across such an upgrade, so the init effect early-returned,
 *   `formItemsMeta` kept the pre-upgrade items (still pointing at the *old* definition's field
 *   IDs), every `formDefinitionFields.find(...)` lookup in `FormSection` missed, and the whole
 *   form rendered as `null` — a silently blank body under a header reading "Version 2", which a
 *   subsequent Save Draft would then persist as a v2 header carrying v1 item mappings.
 *
 * `save()` arms `adoptingOwnSaveRef` through this same function, so the armed string and the
 * computed signature cannot drift apart. Keep it that way: they are compared for exact equality.
 */
function existingInitSignature(formDefinitionID: string | null | undefined, items: FormItem[] | null | undefined): string {
    return `existing:${formDefinitionID ?? ''}:${(items ?? []).map((item) => item.ID).join('|')}`;
}

function readDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('The attachment could not be read'));
        reader.readAsDataURL(file);
    });
}

function ManageFormModal({ onSave, formHeaderToManage, isNew, onRenew }: ManageFormModalProps) {
    const toast = useContext(ToastContext);

    const [confirmOptions, setConfirmOptions] = useState(Helper.initialiseConfirmOptions());
    // Upgrading swaps the whole header without going through the parent, so it is the one case
    // where the header is not simply the prop. Everything else (notably the signature pre-save)
    // routes the new header back through `onSave`, and it must be read straight off the prop —
    // mirroring it into state would let `formHeader` and `isNew` disagree for one render, which
    // briefly empties the item set and remounts every field.
    const [upgradedHeader, setUpgradedHeader] = useState<FormHeader | null>(null);
    const formHeader = upgradedHeader ?? formHeaderToManage;
    const [saving, setSaving] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);

    const { formDefinitionFields, formDataOptions, isLoading, initialFormItemsMeta } = useFormDefinitionData({ formHeader, isNew });

    // The item *set* is React state of its own: adding/removing a repeatable-section instance
    // changes which items exist, which `useFormDefinitionData` (which only derives the initial
    // set from the server / blank-item generation) has no way to know about.
    const [formItemsMeta, setFormItemsMeta] = useState<FormItem[]>([]);

    // Bumped from `onFieldTouched` purely to force a re-render (see its own comment) — the section
    // status dots are computed fresh from `form.getValues()` on every render, so they only go
    // stale when nothing re-renders `ManageFormModal` at all, not from anything this value itself
    // needs to carry.
    const [, setStatusRefreshTick] = useState(0);

    const form = useForm<Record<string, FormFieldValue>>({
        mode: 'uncontrolled',
        initialValues: {},
        validate: buildValidator(formItemsMeta, formDefinitionFields),
    });

    const autosave = useFormAutosave({ formHeader, isNew, formItemsMeta });
    const [restorePending, setRestorePending] = useState(!!autosave.pendingRestore);

    // Values the form loaded with, kept so "Discard" on the recovery prompt can put them back.
    const baselineValuesRef = useRef<Record<string, FormFieldValue>>({});

    // Identifies *which* form the store currently holds. Re-initialising is driven off this rather
    // than off object identity so that a React Query background refetch returning an equivalent
    // payload cannot silently regenerate blank item GUIDs and wipe what the user has typed.
    const initSignature = useMemo(
        () => (isNew
            ? `new:${formDefinitionFields.map((f) => f.ID).join('|')}`
            : existingInitSignature(formHeader.FormDefinitionID, formHeader.FormItems)),
        [isNew, formDefinitionFields, formHeader.FormDefinitionID, formHeader.FormItems],
    );
    const appliedSignatureRef = useRef<string | null>(null);
    // Holds the exact `initSignature` we expect to see arrive when a header this component saved
    // itself (`save(true)`, the signature pre-save) comes back in as a new prop.
    //
    // This deliberately stores the expected signature rather than a bare boolean. A boolean could
    // outlive the save it was meant to cover: an existing Draft's pre-save returns the same
    // FormItem IDs, so `initSignature` does not change, the effect below early-returns before the
    // flag is consumed, and the flag stays set. A later, unrelated re-initialisation — notably
    // Upgrade, which brings a new definition and (whenever the structure changes) a new item set —
    // would then consume the stale flag, skip `form.setValues(values)`, render the upgraded form
    // blank, and let the next Save persist `null` over every value carried across the upgrade.
    // Comparing against the specific signature makes that impossible: only the one re-init this
    // guard was armed for can match it.
    const adoptingOwnSaveRef = useRef<string | null>(null);

    useEffect(() => {
        if (isLoading) return;
        if (appliedSignatureRef.current === initSignature) {
            // The store already holds this exact form, so there is nothing to re-seed and nothing
            // to suppress. Any pre-save we were waiting on has landed (it came back with the same
            // item IDs), so disarm the guard here too rather than letting it leak forward.
            adoptingOwnSaveRef.current = null;
            return;
        }
        appliedSignatureRef.current = initSignature;

        const hydrated = hydrateFormItems(initialFormItemsMeta, formDefinitionFields);
        const values = valuesFromItems(hydrated);
        baselineValuesRef.current = values;
        setFormItemsMeta(hydrated);
        // `form.setValues` bumps `form.key(...)`, which remounts every field binding. That is
        // exactly what a genuine reload (first load, upgrade) needs, but it is wrong when the
        // "new" header is simply the one this component just saved: signing an unsaved form
        // pre-saves it and the persisted header comes straight back in as a prop, and remounting
        // there would throw away the Signature field's open capture modal.
        const adopting = adoptingOwnSaveRef.current;
        adoptingOwnSaveRef.current = null;
        if (adopting !== null && adopting === initSignature) return;
        if (!sameValues(values, form.getValues())) form.setValues(values);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, initSignature, initialFormItemsMeta, formDefinitionFields]);

    // A pending draft is applied only once, and only after `formItemsMeta` exists — `restoreValues`
    // needs the item metadata both to know which stored strings are really Dates and to map the
    // snapshot's composite keys back onto this session's item IDs.
    const restoreAppliedRef = useRef(false);
    useEffect(() => {
        if (restoreAppliedRef.current) return;
        if (!autosave.pendingRestore) return;
        if (formItemsMeta.length === 0) return;
        restoreAppliedRef.current = true;
        const { values, addedItems } = autosave.restoreValues(autosave.pendingRestore);
        // Repeatable-section instances the user had added before the crash are not in the freshly
        // computed item set, so they are put back before their values are written — otherwise the
        // values would sit in the store with nothing rendering them.
        if (addedItems.length > 0) setFormItemsMeta((current) => [...current, ...addedItems]);
        form.setValues(values);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formItemsMeta]);

    // A header pushed in from the parent always wins over a locally upgraded one.
    useEffect(() => {
        setUpgradedHeader(null);
    }, [formHeaderToManage]);

    // `SCModal` used to register/deregister itself in `openModalsAtom` while open, which
    // `ScDrawer` reads to disable its own `closeOnEscape` whenever a modal is stacked on top of it
    // (otherwise Escape would fall through and close the underlying drawer instead of just this
    // modal). This component is only ever mounted while it should be counted as "open", so register
    // once on mount and deregister on unmount rather than tracking an `open` prop.
    const [, setOpenModals] = useAtom(openModalsAtom);
    useEffect(() => {
        const uid = crypto.randomUUID();
        setOpenModals((current) => [...current, uid]);
        return () => setOpenModals((current) => current.filter((x) => x !== uid));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sectionsToRender = useMemo(() => buildSectionTree(formItemsMeta), [formItemsMeta]);

    // `undefined` is a distinct sentinel from `null`: `undefined` means "never bootstrapped yet"
    // (nothing has been opened/closed by the user or by code), while `null` means "the user
    // deliberately collapsed the open section" — Mantine's single-mode `Accordion` reports `null`
    // on `onChange` when you click an already-open control. Conflating the two (both as `null`)
    // was Finding 1: it made the accordion self-heal a deliberate collapse right back open.
    const [openSectionKey, setOpenSectionKey] = useState<string | null | undefined>(undefined);

    const hasMultipleSections = sectionsToRender.length > 1;

    // Self-healing rather than an effect: fall back to the first section only when we've never
    // bootstrapped (`undefined`) or the stored key no longer matches any rendered section (e.g.
    // its instance was just removed). A deliberate `null` — the user collapsed the open section —
    // is respected as-is and leaves nothing open.
    const effectiveOpenKey = openSectionKey === undefined
        || (openSectionKey !== null && !sectionsToRender.some((sg) => getSectionNavKey(sg) === openSectionKey))
        ? (sectionsToRender[0] ? getSectionNavKey(sectionsToRender[0]) : null)
        : openSectionKey;

    // Recomputed on every render rather than memoized: the form runs in `mode: 'uncontrolled'`
    // specifically so typing does not re-render this component (see the memo-boundary note above),
    // so a memoized version would show stale status most of the time. Computing it plainly here
    // means the dots are current as of whatever render already happened to occur (opening/closing
    // a section, adding/removing an instance, load) — a deliberate, documented tradeoff rather than
    // fighting that performance design with new per-keystroke re-render plumbing.
    const statusesByKey: Record<string, ReturnType<typeof sectionStatus>> = {};
    const currentValues = form.getValues();
    sectionsToRender.forEach((sg) => {
        // A Completed form is read-only end-to-end, so every section shows as complete regardless
        // of its individual field state (e.g. a field left blank by a since-removed FormRule).
        statusesByKey[getSectionNavKey(sg)] = formHeader.FormStatus === Enums.FormStatus.Completed
            ? 'complete'
            : sectionStatus(sg.formItems, form.errors ?? {}, formDefinitionFields, currentValues);
    });

    // Mantine's Accordion.Panel expand/collapse transition means the layout can still be moving
    // for a beat after a section opens — either its own panel growing, or (in this single-open
    // Accordion) a *previously* open section's panel collapsing above it, which shifts everything
    // below, including the section just opened. Scrolling immediately targets a position that's
    // about to move.
    //
    // A fixed `setTimeout` tied to the transition's nominal 200ms duration was tried here before,
    // but wasn't reliable enough — the CSS transition doesn't necessarily *start* the instant this
    // code runs (React's own re-render has to land first), so a delay measured from "now" can
    // still fire before the transition actually finishes, especially under load. This instead
    // polls the target's own screen position every frame and waits until it's stopped moving,
    // which adapts to however long the transition actually takes rather than guessing.
    //
    // (An even earlier version scrolled once immediately *and* once again after a fixed delay, for
    // snappier feedback — but a second `scrollIntoView({behavior:'smooth'})` fired while the first
    // smooth scroll was still animating a long distance could get its target position recomputed
    // mid-flight and stop short, landing centre-ish instead of at the start. Scrolling exactly
    // once, only once the position has settled, avoids that entirely.)
    const scrollElementIntoViewOnceStable = (resolveElement: () => Element | null, options: ScrollIntoViewOptions) => {
        const REQUIRED_STABLE_FRAMES = 3;
        const MAX_FRAMES = 30; // ~500ms at 60fps - a safety net so this can never hang indefinitely
        let lastTop: number | null = null;
        let stableFrames = 0;
        let framesChecked = 0;

        const check = () => {
            const element = resolveElement();
            if (!element) return; // target never (or no longer) exists - give up quietly
            const top = element.getBoundingClientRect().top;
            stableFrames = lastTop !== null && Math.abs(top - lastTop) < 0.5 ? stableFrames + 1 : 0;
            lastTop = top;
            framesChecked += 1;

            if (stableFrames >= REQUIRED_STABLE_FRAMES || framesChecked >= MAX_FRAMES) {
                element.scrollIntoView(options);
                return;
            }
            requestAnimationFrame(check);
        };
        requestAnimationFrame(check);
    };

    const scrollSectionAnchorIntoView = (key: string) => {
        scrollElementIntoViewOnceStable(
            () => document.getElementById(`form-section-anchor-${key}`),
            { behavior: 'smooth', block: 'start' },
        );
    };

    const navigateToSection = (key: string) => {
        setOpenSectionKey(key);
        scrollSectionAnchorIntoView(key);
    };

    // A closed `Accordion.Panel` still renders its fields (Mantine's `Collapse` keeps children
    // mounted, just visually hidden/`inert`), so `scrollToFirstError` alone can "succeed" while
    // scrolling to something the user can't see or focus. Open the section owning the first error
    // before scrolling to it, same settle-then-scroll-once reasoning as `scrollSectionAnchorIntoView`
    // above — only needed when a section was actually (re)opened, since only that path has a
    // transition to wait out; an error inside the already-open section scrolls immediately.
    const scrollToFirstErrorAfterOpeningSection = (errors: FormErrors) => {
        const firstErrorKey = Object.keys(errors)[0];
        let openedSection = false;
        if (firstErrorKey) {
            const sectionKey = findSectionKeyForError(firstErrorKey, sectionsToRender, formDefinitionFields, form.getValues());
            if (sectionKey) {
                setOpenSectionKey(sectionKey);
                openedSection = true;
            }
        }
        if (openedSection && firstErrorKey) {
            scrollElementIntoViewOnceStable(
                () => document.querySelector(`[data-form-item-id="${CSS.escape(firstErrorKey)}"]`),
                { behavior: 'smooth', block: 'center' },
            );
        } else {
            requestAnimationFrame(() => scrollToFirstError(errors));
        }
    };

    // NOTE on the `React.memo` boundaries downstream (FormSection / FormFieldBinding /
    // FormFieldRenderer): the callbacks below — `onFieldTouched`, `availableOptions`, `onUpload`,
    // `resolveImageSrc`, `buildSignatureContext`, `onAddInstance`, `onRemoveInstance` — plus the
    // `form` object itself are recreated on every render of this component, so every one of those
    // memo boundaries misses whenever this component re-renders. Those memos therefore only prevent
    // re-renders from parent state changes that don't also change these — `onFieldTouched`'s own
    // `setStatusRefreshTick` call below is one such trigger, firing once per field "touch" (blur for
    // most types, each discrete change for the no-blur ones — see `NO_BLUR_TYPES`), never per
    // keystroke. This is a documented, bounded limitation, not load-bearing protection: the
    // per-keystroke isolation comes from `FormFieldBinding`'s local state + `mode: 'uncontrolled'`,
    // not from these memos. Stabilising all seven callbacks and the `form` identity is deliberate
    // follow-up scope.
    // The store is keyed by `FormItem.ID`, which for a never-saved form is regenerated on every
    // mount — so the snapshot is re-keyed by the mount-independent composite on the way out. See
    // `buildAutosaveComposite`; `restoreValues` performs the inverse mapping.
    const onFieldTouched = () => {
        const values = form.getValues();
        const snapshotValues: Record<string, FormFieldValue> = {};
        formItemsMeta.forEach((item) => {
            snapshotValues[buildAutosaveComposite(item)] = values[item.ID] ?? null;
        });
        autosave.scheduleAutosave(snapshotValues);
        // Without this, the section status dots (computed fresh from `form.getValues()` on every
        // render — see `statusesByKey` above) only refresh on whatever re-render happens to occur
        // for some unrelated reason (the very first edit's clean→dirty flip, opening/closing a
        // section, etc.) — so a second edit in the same section looked like it hadn't registered
        // until something else forced a re-render. Bumping this on every field-touch keeps the dots
        // current as of the same "commit" moment autosave already uses.
        setStatusRefreshTick((tick) => tick + 1);
    };

    const onAddInstance = (sectionGroup: SectionGroupNode) => {
        const newItems = addSectionGroupInstance(sectionGroup);
        setFormItemsMeta([...formItemsMeta, ...newItems]);
        newItems.forEach((item) => form.setFieldValue(item.ID, decodeFormItemValue(item.DataResult, item.DataType ?? '')));
        const newTopLevelItem = newItems.find((item) => item.SectionID === sectionGroup.sectionID);
        if (newTopLevelItem) setOpenSectionKey(getSectionNavKey({ sectionID: newTopLevelItem.SectionID ?? null, sectionGroup: newTopLevelItem.SectionGroup ?? 0 }));
    };

    const performRemoveInstance = (sectionGroup: SectionGroupNode) => {
        setFormItemsMeta(removeSectionGroupInstance(formItemsMeta, sectionsToRender, sectionGroup));
    };

    const onRemoveInstance = (sectionGroup: SectionGroupNode) => {
        if (!isSectionGroupAnswered(sectionGroup, form.getValues())) {
            performRemoveInstance(sectionGroup);
            return;
        }
        setConfirmOptions({
            ...Helper.initialiseConfirmOptions(),
            display: true,
            heading: 'Remove this section?',
            text: 'This section has answers that will be lost if you continue.',
            confirmButtonText: 'Remove',
            onConfirm: () => performRemoveInstance(sectionGroup),
        });
    };

    // Ported from the legacy editor's `getDataOptions`: a field's own custom-CSV `DataOption`,
    // overridden by a per-form `FormDataOptions` entry matched on `MasterID` when one exists.
    const availableOptions = (field: FormDefinitionField): string[] => {
        if (!field) return [];
        let dataOption = field.DataOption;
        const match = formDataOptions?.find((x: any) => x.ItemMasterID === field.MasterID);
        if (match) dataOption = match.DataOption;
        const options: string[] = Helper.deserializeCustomCSV(dataOption) ?? [];
        // Real form definitions do contain duplicated and blank options. The legacy Kendo-based
        // dropdown rendered every duplicate as its own separate, selectable entry, so blank
        // entries are dropped here but duplicates are preserved — `Fields/FormFieldSelect.tsx`/
        // `FormFieldMultiSelect.tsx` are responsible for giving Mantine's Select/MultiSelect a
        // unique key per duplicate so it doesn't collapse/throw on them.
        return options.filter((option) => option !== null && option !== undefined && `${option}`.trim() !== '');
    };

    // Attachment URLs are fetched lazily and cached in a ref; the tick state exists purely to
    // re-render once a URL lands (which also gives `resolveImageSrc` a fresh identity, so the
    // memoised image fields pick the new URL up).
    const [, setImageSrcTick] = useState(0);
    const imageSrcRef = useRef<Record<string, string>>({});
    const resolveImageSrc = (attachmentID: string): string => {
        if (!attachmentID || attachmentID.length !== 36) return '';
        const cached = imageSrcRef.current[attachmentID];
        if (cached === 'PENDING') return '';
        if (cached) return cached;

        imageSrcRef.current[attachmentID] = 'PENDING';
        Fetch.get({ url: '/Attachment', params: { id: attachmentID } }).then((attachment: any) => {
            if (attachment && attachment.Url) {
                imageSrcRef.current[attachmentID] = attachment.Url;
                setImageSrcTick((tick) => tick + 1);
            } else {
                // No usable URL in the response - clear the sentinel so the next render retries
                // instead of leaving this attachment permanently blank for the session.
                delete imageSrcRef.current[attachmentID];
            }
        }).catch(() => {
            delete imageSrcRef.current[attachmentID];
        });
        return '';
    };

    const [, setSignaturePreviewTick] = useState(0);
    const signaturePreviewRef = useRef<Record<string, SignaturePreview | 'PENDING'>>({});

    const buildSignaturePreview = (signature: any): SignaturePreview | null => {
        const url = signature?.Attachment?.Url || signature?.Attachment?.UrlThumb;
        if (!url) return null;
        return { url, signedBy: signature.SignedBy ?? '', signedDate: signature.SignedDate ?? '', hidden: !!signature.SignatureTemplate?.HideSignature };
    };

    const resolveSignaturePreview = (signatureID: string): SignaturePreview | null => {
        if (!signatureID) return null;
        const cached = signaturePreviewRef.current[signatureID];
        if (cached === 'PENDING') return null;
        if (cached) return cached;

        signaturePreviewRef.current[signatureID] = 'PENDING';
        signatureService.getSignature(signatureID).then((signature: any) => {
            const preview = buildSignaturePreview(signature);
            if (preview) {
                signaturePreviewRef.current[signatureID] = preview;
                setSignaturePreviewTick((tick) => tick + 1);
            } else {
                // No preview-able attachment yet (e.g. still processing server-side) - clear the
                // sentinel so the next render retries instead of leaving this signature blank forever.
                delete signaturePreviewRef.current[signatureID];
            }
        }).catch(() => {
            delete signaturePreviewRef.current[signatureID];
        });
        return null;
    };

    // A re-sign reuses the *same* signature ID (only its Attachment changes), so
    // `resolveSignaturePreview`'s ID-keyed cache above never sees it as new and never refetches —
    // the preview would only catch up after a full page reload. `FormFieldSignature` already gets
    // handed the freshly-saved record on every capture/re-sign, so write it straight into the cache
    // here instead of relying on a refetch that a same-ID cache hit would just skip.
    const onSignatureSaved = (signature: any) => {
        if (!signature?.ID) return;
        const preview = buildSignaturePreview(signature);
        if (preview) {
            signaturePreviewRef.current[signature.ID] = preview;
            setSignaturePreviewTick((tick) => tick + 1);
        }
    };

    const onUpload = async (file: File): Promise<string> => {
        const dataUrl = await readDataUrl(file);

        if (!dataUrl.startsWith('data:image/')) {
            toast.setToast!!!({ message: 'The attachment must be an image', show: true, type: Enums.ToastType.error });
            throw new Error('The attachment must be an image');
        }

        const fileSizeSetting: any = await OptionService.getOption('System Settings', 'File Upload Size');
        const fileSizeUnit = fileSizeSetting ? fileSizeSetting.Unit : 'mb';
        const fileSizeValue = fileSizeSetting ? parseInt(fileSizeSetting.OptionValue) : 2;

        const b64 = dataUrl.replace(/^data:.+;base64,/, '');
        let uploadLength = 2;
        if (fileSizeUnit === 'kb') uploadLength = b64.length / 1024;
        else if (fileSizeUnit === 'mb') uploadLength = b64.length / 1024 / 1024;
        else if (fileSizeUnit === 'gb') uploadLength = b64.length / 1024 / 1024 / 1024;

        // base64 encodes 6 bits per 8-bit character, so the real file size is 3/4 of this.
        uploadLength *= Constants.base64BitScalingFactor;

        if (uploadLength > parseFloat(fileSizeValue as any)) {
            toast.setToast!!!({ message: `The attachment must be smaller than ${fileSizeValue}${fileSizeUnit}`, show: true, type: 'error' });
            throw new Error('The attachment is too large');
        }

        const attachmentRes = await Fetch.post({
            url: '/Attachment',
            params: {
                AttachmentType: Enums.AttachmentType.Image,
                Description: file.name,
                FileName: file.name,
                FileBase64: b64,
                ItemID: formHeader.ID,
                Module: Enums.Module.FormHeader,
            },
            toastCtx: toast,
        });

        if (!attachmentRes || !attachmentRes.ID) throw new Error('The attachment could not be uploaded');
        return attachmentRes.ID;
    };

    const isExpired = () => !!formHeader?.ExpireDate && Time.now().valueOf() > Time.parseDate(formHeader.ExpireDate).valueOf();
    const willExpire = () => !!formHeader?.ExpireDate && Time.now().valueOf() <= Time.parseDate(formHeader.ExpireDate).valueOf();

    const disableField = () => formHeader.FormStatus === Enums.FormStatus.Completed
        && (formHeader.FormDefinition?.ExpireTimespan !== 0 || formHeader.FormDefinition?.FormRule !== Enums.FormRule.Customer);

    const buildFormItemsPayload = (): FormItem[] => {
        const values = form.getValues();
        return formItemsMeta.map((meta) => ({ ...meta, DataResult: encodeFormItemValue(values[meta.ID] ?? null, meta.DataType ?? '') }));
    };

    // The legacy editor lazily copied the definition's name/description onto the header the first
    // time it rendered; do the same on the way out so saved headers keep carrying them.
    const headerToSave = (): FormHeader => ({
        ...formHeader,
        FormDefinitionDisplayName: formHeader.FormDefinitionDisplayName || formHeader.FormDefinition?.Name,
        FormDefinitionDescription: formHeader.FormDefinitionDescription || formHeader.FormDefinition?.Description,
    });

    const save = async (ignoreOnSave = false) => {
        // Matches the legacy editor: a Draft may be saved incomplete; only a Completed form that is
        // being updated has to pass validation again.
        if (formHeader.FormStatus === Enums.FormStatus.Completed) {
            const validation = form.validate();
            if (validation.hasErrors) {
                scrollToFirstErrorAfterOpeningSection(validation.errors);
                return;
            }
        }

        setSaving(true);
        const params = { FormHeader: headerToSave(), FormItems: buildFormItemsPayload(), IsNew: isNew };
        const result = isNew
            ? await Fetch.post({ url: '/Form', params, toastCtx: toast })
            : await Fetch.put({ url: '/Form', params, toastCtx: toast });

        if (result && result.ID) {
            autosave.clearOnCleanExit();
            if (ignoreOnSave) {
                // The header we hand to `onSave` is put back on this component verbatim as
                // `formHeaderToManage` (with `_isNew: false`), so the `initSignature` it will
                // produce is known exactly here. Arming the guard with that precise value means it
                // can only suppress the re-seed for *this* save, and expires harmlessly otherwise.
                // Built through the same `existingInitSignature` helper the memo above uses, so the
                // two can never fall out of step.
                adoptingOwnSaveRef.current = existingInitSignature(result.FormDefinitionID, result.FormItems);
            }
            !ignoreOnSave && toast.setToast!!!({ message: 'Form captured successfully', show: true, type: 'success' });
            onSave(ignoreOnSave ? { ...result, _isNew: false } : null);
        }
        setSaving(false);
    };

    const complete = async () => {
        setSaving(true);

        const validation = form.validate();
        if (validation.hasErrors) {
            setSaving(false);
            scrollToFirstErrorAfterOpeningSection(validation.errors);
            return;
        }

        const params = {
            FormHeader: { ...headerToSave(), FormStatus: Enums.FormStatus.Completed, CompletedDate: Time.toISOString(Time.now()) },
            FormItems: buildFormItemsPayload(),
            IsNew: isNew,
        };
        const result = isNew
            ? await Fetch.post({ url: '/Form', params, toastCtx: toast })
            : await Fetch.put({ url: '/Form', params, toastCtx: toast });

        if (result && result.ID) {
            autosave.clearOnCleanExit();
            toast.setToast!!!({ message: 'Form completed successfully', show: true, type: 'success' });
            onSave(null);
        } else {
            setSaving(false);
        }
    };

    const completeCheck = () => {
        const validation = form.validate();
        if (validation.hasErrors) {
            scrollToFirstErrorAfterOpeningSection(validation.errors);
            return;
        }

        setConfirmOptions({
            ...Helper.initialiseConfirmOptions(),
            confirmButtonText: 'Complete',
            display: true,
            heading: 'Complete the form?',
            text: formHeader.FormDefinition?.ExpireTimespan === 0 ? '' : 'Form will not be editable once completed.',
            onConfirm: complete,
        });
    };

    const cancel = () => {
        autosave.clearOnCleanExit();
        onSave(null);
    };

    // Three cases `sameValues(baselineValuesRef.current, form.getValues())` alone cannot see:
    //
    // - An unconsumed crash-recovery draft (`restorePending`): the form store still equals the
    //   baseline because the recovered draft hasn't been applied to it yet (that only happens once
    //   the user picks Restore), so `sameValues` reports clean even though discarding now would
    //   silently drop the recovered draft. Outside-click closes the drawer by default, so without
    //   this an accidental click before Restore/Discard destroys the draft with no confirmation.
    // - A removed repeatable-section instance: `onRemoveInstance` only updates `formItemsMeta`, not
    //   the form's uncontrolled value store, so `form.getValues()` still carries the removed
    //   instance's stale keys/values and `sameValues`'s key-count check never trips. Comparing the
    //   baseline's item-ID set against `formItemsMeta`'s current one catches that directly, without
    //   having to reach into `@mantine/form`'s uncontrolled store to delete keys.
    // - An *added* repeatable-section instance: `onAddInstance` writes the new items' (blank)
    //   values into the form store, so `form.getValues()` now carries keys `baselineValuesRef`
    //   never had — `sameValues`'s key-count check trips immediately, on the add itself, even
    //   though nothing has actually been typed into the new instance yet. Comparing only the keys
    //   both sides share sidesteps that; whether the new instance itself is dirty is answered
    //   separately, below, by whether any of its own items have been answered — the exact same
    //   semantics `isSectionGroupAnswered` uses to decide whether removing one needs confirmation.
    const isDirty = () => {
        if (restorePending) return true;
        const currentItemIDs = new Set(formItemsMeta.map((item) => item.ID));
        const baselineItemIDs = new Set(Object.keys(baselineValuesRef.current));
        if (Object.keys(baselineValuesRef.current).some((id) => !currentItemIDs.has(id))) return true;

        const currentValues = form.getValues();
        const currentBaselineSubset: Record<string, FormFieldValue> = {};
        baselineItemIDs.forEach((id) => { currentBaselineSubset[id] = currentValues[id]; });
        if (!sameValues(baselineValuesRef.current, currentBaselineSubset)) return true;

        const addedItems = formItemsMeta.filter((item) => !baselineItemIDs.has(item.ID));
        return sectionStats(addedItems, currentValues).answered > 0;
    };

    // Shared by both exit paths — the explicit Cancel/Close button below, and the navigation
    // blocker further down that catches the side nav being clicked while the drawer is open —
    // so leaving a dirty form always shows the identical prompt no matter how it's triggered.
    const promptDiscardChanges = (onConfirmed: () => void, onCancelled: () => void = () => {}) => {
        setConfirmOptions({
            ...Helper.initialiseConfirmOptions(),
            display: true,
            heading: 'Discard changes?',
            text: 'You have unsaved changes that will be lost if you continue.',
            confirmButtonText: 'Discard',
            onConfirm: onConfirmed,
            onCancel: onCancelled,
        });
    };

    const attemptCancel = () => {
        if (!isDirty()) {
            cancel();
            return;
        }
        promptDiscardChanges(cancel);
    };

    // Clicking a side nav link navigates away without ever going through `attemptCancel` — the
    // drawer just gets unmounted out from under the form. `useBlocker` intercepts that at the
    // router level so an in-progress edit gets the same discard-or-stay prompt as the explicit
    // Cancel/Close button, rather than silently losing changes.
    const navigationBlocker = useBlocker({
        shouldBlockFn: () => isDirty(),
        withResolver: true,
    });

    useEffect(() => {
        if (navigationBlocker.status !== 'blocked') return;
        const { proceed, reset } = navigationBlocker;
        promptDiscardChanges(() => {
            cancel();
            proceed();
        }, reset);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigationBlocker.status]);

    // Signature capture needs a persisted FormHeader to attach to, so an unsaved (or still-editable)
    // form is written out first — ported from the legacy Sign Here handler.
    const ensurePersisted = async () => {
        if (isNew || !formHeader.CompletedDate) await save(true);
    };

    const buildSignatureContext = (item: FormItem): SignatureFieldContext => ({
        ensurePersisted,
        itemID: item.ID,
    });

    const printFormHeader = () => {
        setIsPrinting(true);
        DownloadService.downloadFile('GET', `/Form/PrintForm?formHeaderID=${formHeader.ID}`, null, true, false, '', '', null, false, () => {
            setIsPrinting(false);
        });
    };

    const canUpgrade = () => !!formHeader
        && formHeader.FormStatus === Enums.FormStatus.Draft
        && !!formHeader.LatestFormDefinitionID
        && formHeader.LatestFormDefinitionID !== formHeader.FormDefinitionID;

    const onUpgrade = () => {
        setConfirmOptions({
            ...Helper.initialiseConfirmOptions(),
            display: true,
            confirmButtonText: 'Upgrade Form',
            heading: 'Confirm upgrade form?',
            text: 'The form will take on the new structure',
            onConfirm: async () => {
                const resultFormHeader = await Fetch.post({
                    url: '/Form/Upgrade',
                    params: { ...headerToSave(), FormItems: buildFormItemsPayload() },
                });
                // The new header carries a new FormDefinitionID and a new FormItem set, so the
                // definition query re-runs and the init effect rebuilds the store from scratch.
                if (resultFormHeader && resultFormHeader.ID) setUpgradedHeader(resultFormHeader);
            },
        });
    };

    const displayName = formHeader?.FormDefinitionDisplayName || formHeader?.FormDefinition?.Name;
    const displayDescription = formHeader?.FormDefinitionDescription || formHeader?.FormDefinition?.Description;

    const drawerSize = 'clamp(900px, 100vw - 72px, 1400px)';

    // The prop bundle every `FormSection` call site below needs, regardless of whether it's the
    // single-section fallback, the borderless "General" block, or an accordion item's panel.
    const fieldWiring = {
        formDefinitionFields,
        form,
        onAddInstance,
        onRemoveInstance,
        onFieldTouched,
        availableOptions,
        onUpload,
        resolveImageSrc,
        resolveSignaturePreview,
        onSignatureSaved,
        buildSignatureContext,
        disabled: disableField(),
    };

    const headerSection = (
        <div className={styles.header}>
            <Flex align="start" justify="space-between" gap="sm" wrap="wrap">
                <Title order={2}>{displayName} {isExpired() ? '(EXPIRED)' : ''}</Title>
                <Group gap="xs" wrap="wrap" align="center" justify="flex-end">
                    <Text c="gray.7" size="sm" inline className={styles.version}>Version {formHeader?.FormDefinition?.Version}</Text>

                    {canUpgrade() && (
                        <Anchor component="button" type="button" size="sm" fw={700} onClick={onUpgrade}>
                            Update to Latest Version
                        </Anchor>
                    )}

                    {formHeader && !formHeader._isNew && (
                        <Button variant="default" size="xs" leftSection={<IconPrinter size={14} />} loading={isPrinting} onClick={printFormHeader}>
                            Print
                        </Button>
                    )}

                    {formHeader && formHeader.FormStatus === Enums.FormStatus.Completed && (isExpired() || willExpire()) && (
                        <Button variant="default" size="xs" leftSection={<IconRepeat size={14} />} onClick={() => onRenew(formHeaderToManage)}>
                            Renew
                        </Button>
                    )}

                    <CloseButton onClick={attemptCancel} aria-label="Close" ml="xs" />
                </Group>
            </Flex>
            {displayDescription && <Text size="sm" c="gray.7" mt="sm">{displayDescription}</Text>}
        </div>
    );

    const canUpdateCompleted = formHeader?.FormStatus === Enums.FormStatus.Completed
        && formHeader.FormDefinition?.ExpireTimespan === 0
        && formHeader.FormDefinition?.FormRule === Enums.FormRule.Customer;

    // The drawer footer now sits flush with the bottom edge (see the `body` style override on
    // `ScDrawer` below, which zeroes out its default chat-widget safe-area padding) rather than
    // leaving that whole strip empty — so the right-hand action buttons get their own right margin
    // instead, just enough to clear the HubSpot widget's floating bottom-right bubble.
    const footerSection = (
        <Flex justify="space-between" align="end" w="100%" wrap="wrap" gap="sm">
            <Button variant="outline" onClick={attemptCancel}>Cancel</Button>
            {formHeader?.FormStatus === Enums.FormStatus.Draft && (
                <Flex justify="end" align="end" gap="xs" direction={{ base: 'column', xs: 'row' }} style={{ marginRight: Constants.chatWidgetSafeArea }}>
                    <Button variant="outline" onClick={() => save(false)} disabled={saving}>Save Draft</Button>
                    <Button onClick={completeCheck} disabled={saving}>Complete</Button>
                </Flex>
            )}
            {canUpdateCompleted && <Button onClick={() => save(false)} disabled={saving} style={{ marginRight: Constants.chatWidgetSafeArea }}>Update</Button>}
        </Flex>
    );

    if (isLoading) {
        return (
            <ScDrawer opened onClose={attemptCancel} size={drawerSize}>
                <Flex justify="center" align="center" p="xl"><Loader /></Flex>
            </ScDrawer>
        );
    }

    return (
        <ScDrawer
            opened
            onClose={attemptCancel}
            size={drawerSize}
            styles={{
                // Mantine's own title/close-button bar is redundant now that headerSection
                // (with its own CloseButton, above) lives inside the scrollable body — a second,
                // non-scrolling bar above it read as a blank strip permanently covering the real
                // header. Hidden here rather than in ScDrawer itself, which other drawers still
                // rely on for their own title bar.
                header: { display: 'none' },
                // ScDrawer reserves `constants.chatWidgetSafeArea` of bottom padding by default so
                // the HubSpot widget bubble never sits over a drawer's own content — here that left
                // a large empty strip below the footer instead, since the footer is already the
                // bottommost thing in this drawer. Zeroed out in favour of shifting just the
                // right-hand action buttons left instead (see footerSection above).
                body: { display: 'flex', flexDirection: 'column', height: '100%', paddingBottom: 0 },
                content: { overflowY: 'hidden' },
            }}
        >
            <div className={styles.formBody}>
                {headerSection}

                <DraftRecoveryPrompt
                    open={restorePending}
                    onRestore={() => setRestorePending(false)}
                    onDiscard={() => {
                        autosave.discardDraft();
                        form.setValues(baselineValuesRef.current);
                        setRestorePending(false);
                    }}
                />

                {!restorePending && !hasMultipleSections && sectionsToRender.map((sectionGroup) => (
                    <FormSection
                        key={getSectionNavKey(sectionGroup)}
                        sectionGroup={sectionGroup}
                        {...fieldWiring}
                    />
                ))}

                {!restorePending && hasMultipleSections && (
                    <div className={styles.layout}>
                        <FormTableOfContents
                            sections={sectionsToRender}
                            statusesByKey={statusesByKey}
                            activeKey={effectiveOpenKey}
                            onNavigate={navigateToSection}
                        />
                        <div className={styles.contentCol}>
                            {/* The "General" group (ungrouped items with no real section) reads as plain
                                fields, not as a bordered/collapsible card — it isn't a real section, so it
                                stays outside the Accordion entirely and is always visible. It still gets a
                                scroll anchor so its TOC entry works the same as every other section. */}
                            {sectionsToRender.filter((sg) => sg.sectionID === null).map((sectionGroup) => (
                                <div key={getSectionNavKey(sectionGroup)} id={`form-section-anchor-${getSectionNavKey(sectionGroup)}`}>
                                    <FormSection sectionGroup={sectionGroup} {...fieldWiring} />
                                </div>
                            ))}
                            <Accordion value={effectiveOpenKey} onChange={(value) => setOpenSectionKey(value)}>
                                {sectionsToRender.filter((sg) => sg.sectionID !== null).map((sectionGroup) => {
                                    const key = getSectionNavKey(sectionGroup);
                                    const label = getSectionNavLabel(sectionGroup, sectionsToRender);
                                    const status = statusesByKey[key];
                                    const { total, answered } = sectionStats(sectionGroup.formItems, currentValues);
                                    return (
                                        <Accordion.Item
                                            key={key}
                                            value={key}
                                            id={`form-section-anchor-${key}`}
                                            className={key === effectiveOpenKey ? `${styles.accordionItem} ${styles.accordionItemActive}` : styles.accordionItem}
                                        >
                                            {/* Accordion.Control is itself a <button> (the whole row toggles the
                                                panel), so the delete action can't nest inside it — it's a sibling
                                                here instead, landing immediately after Control's own chevron. */}
                                            <Flex align="center" gap="xs" wrap="nowrap">
                                                <Accordion.Control style={{ flexGrow: 1 }}>
                                                    <Flex align="center" gap="xs">
                                                        <span className={styles.statusDot} data-status={status} />
                                                        <Text fw={700} size="sm">{label}</Text>
                                                        {total > 0 && <span className={styles.sectionBadge}>{answered}/{total} filled</span>}
                                                    </Flex>
                                                </Accordion.Control>
                                                {sectionGroup.isDeletable && !fieldWiring.disabled && (
                                                    <ActionIcon
                                                        variant="subtle"
                                                        color="red"
                                                        aria-label="remove section instance"
                                                        onClick={(event) => { event.stopPropagation(); onRemoveInstance(sectionGroup); }}
                                                        mr="sm"
                                                    >
                                                        <IconTrash size={16} />
                                                    </ActionIcon>
                                                )}
                                            </Flex>
                                            <Accordion.Panel>
                                                <FormSection sectionGroup={sectionGroup} {...fieldWiring} suppressHeading suppressBorder suppressDeleteAction />
                                            </Accordion.Panel>
                                        </Accordion.Item>
                                    );
                                })}
                            </Accordion>
                        </div>
                    </div>
                )}
            </div>

            <div className={styles.footer} style={{ flexShrink: 0 }}>{footerSection}</div>

            <ConfirmAction options={confirmOptions} setOptions={setConfirmOptions} />
        </ScDrawer>
    );
}

export default ManageFormModal;
